(() => {
  'use strict';
  const root = document.querySelector('[data-pdp]');
  if (!root || root.dataset.pdpReady) return;
  root.dataset.pdpReady = 'true';
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Gallery: thumbnails scroll the track; the visible slide marks its thumbnail.
  const track = root.querySelector('[data-pdp-track]');
  const thumbs = [...root.querySelectorAll('[data-pdp-thumb]')];
  if (track && thumbs.length) {
    const slides = [...track.children];
    thumbs.forEach(thumb => thumb.addEventListener('click', () => {
      const slide = slides[Number(thumb.dataset.pdpThumb)];
      if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion() ? 'auto' : 'smooth' });
      const back = root.querySelector('[data-pdp-back]');
      if (back && !back.hidden) root.querySelector('[data-pdp-face="front"]')?.click();
    }));
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const index = slides.indexOf(entry.target);
        thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
      }), { root: track, threshold: 0.6 });
      slides.forEach(slide => observer.observe(slide));
    }
  }

  // Quantity stepper and supply line.
  const form = root.querySelector('[data-pdp-form]');
  const quantity = form?.querySelector('[name="quantity"]');
  const supply = root.querySelector('[data-pdp-supply]');
  const updateSupply = () => {
    if (!supply || !quantity) return;
    const count = Math.max(1, parseInt(quantity.value, 10) || 1);
    const days = Number(supply.dataset.days) || 30;
    supply.textContent = count === 1 ? supply.dataset.one : supply.dataset.many.replace('{count}', String(count)).replace('{days}', String(count * days));
  };
  root.querySelectorAll('[data-pdp-qty]').forEach(button => button.addEventListener('click', () => {
    if (!quantity) return;
    const step = Number(quantity.step) || 1;
    const min = Number(quantity.min) || 1;
    const max = quantity.max ? Number(quantity.max) : Infinity;
    const next = (parseInt(quantity.value, 10) || min) + Number(button.dataset.pdpQty) * step;
    quantity.value = String(Math.min(max, Math.max(min, next)));
    updateSupply();
  }));
  quantity?.addEventListener('input', updateSupply);
  quantity?.addEventListener('change', updateSupply);

  // Estimated delivery window in business days.
  const eta = root.querySelector('[data-pdp-eta]');
  const range = eta?.querySelector('[data-eta-range]');
  if (eta && range) {
    const addBusinessDays = (start, count) => {
      const date = new Date(start);
      let added = 0;
      while (added < count) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (day !== 0 && day !== 6) added += 1;
      }
      return date;
    };
    const min = Number(eta.dataset.min);
    const max = Number(eta.dataset.max);
    if (min > 0 && max >= min) {
      const format = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      const now = new Date();
      range.textContent = `${format.format(addBusinessDays(now, min))} – ${format.format(addBusinessDays(now, max))}`;
      eta.hidden = false;
    }
  }

  // Supplement Facts dialog.
  root.querySelectorAll('[data-pdp-open-label]').forEach(button => button.addEventListener('click', event => {
    const dialog = document.getElementById(button.getAttribute('aria-controls'));
    if (dialog && typeof dialog.showModal === 'function') {
      event.preventDefault();
      dialog.showModal();
      document.body.classList.add('dialog-open');
    }
  }));

  // Front / Back (Supplement Facts) switch on the gallery.
  const back = root.querySelector('[data-pdp-back]');
  const faces = [...root.querySelectorAll('[data-pdp-face]')];
  faces.forEach(button => button.addEventListener('click', () => {
    const showBack = button.dataset.pdpFace === 'back';
    if (back) back.hidden = !showBack;
    faces.forEach(face => face.setAttribute('aria-pressed', String(face === button)));
  }));

  // Subscribe and save: the radio choice drives the hidden selling_plan field.
  const planField = form?.querySelector('[data-pdp-plan]');
  form?.querySelectorAll('input[name="dl_purchase"]').forEach(radio => radio.addEventListener('change', () => {
    if (!planField || !radio.checked) return;
    planField.value = radio.value;
    planField.disabled = !radio.value;
  }));

  // Routine add-on: when ticked, add the main product and its routine partner in one request.
  const addon = root.querySelector('[data-pdp-addon]');
  const mainAdd = root.querySelector('[data-pdp-main-add]');
  const payment = root.querySelector('.pdp-pay');
  const purchaseButtons = [...root.querySelectorAll('button.pdp-add')].filter(button => button.form === form);
  const updateAddon = () => {
    if (payment) payment.hidden = Boolean(addon?.checked);
    if (mainAdd?.dataset.available === 'true') {
      purchaseButtons.forEach(button => { button.textContent = addon?.checked ? mainAdd.dataset.pairLabel : mainAdd.dataset.singleLabel; });
    }
  };
  addon?.addEventListener('change', updateAddon);
  updateAddon();
  if (form && addon) {
    let pairPending = false;
    window.addEventListener('submit', async event => {
      if (event.target !== form || (!addon.checked && !pairPending)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (pairPending || !form.reportValidity() || !window.DayloCart) return;
      const data = new FormData(form);
      const main = { id: Number(data.get('id')), quantity: Math.max(1, parseInt(data.get('quantity'), 10) || 1) };
      if (data.get('selling_plan')) main.selling_plan = Number(data.get('selling_plan'));
      const controls = [...new Set([...purchaseButtons, addon, quantity, ...form.querySelectorAll('[data-pdp-qty], input[name="dl_purchase"]')])].filter(Boolean);
      const states = controls.map(control => ({ control, disabled: control.disabled }));
      pairPending = true;
      states.forEach(({ control }) => { control.disabled = true; });
      form.setAttribute('aria-busy', 'true');
      try {
        await window.DayloCart.addItems([main, { id: Number(addon.value), quantity: 1 }], mainAdd);
      } finally {
        states.forEach(({ control, disabled }) => { control.disabled = disabled; });
        form.removeAttribute('aria-busy');
        pairPending = false;
      }
    }, true);
  }

  // Check your batch: lot number to lab report.
  const batch = root.querySelector('[data-pdp-batch]');
  if (batch) {
    let batches = [];
    try { batches = JSON.parse(batch.querySelector('[data-batch-data]')?.textContent || '[]'); } catch (_) { batches = []; }
    const text = name => batch.querySelector(`[data-batch-${name}]`)?.content?.textContent.trim() || '';
    const result = batch.querySelector('[data-batch-result]');
    batch.querySelector('[data-batch-form]')?.addEventListener('submit', event => {
      event.preventDefault();
      const input = event.target.querySelector('input[name="lot"]');
      const lot = (input?.value || '').trim().toUpperCase().replace(/\s+/g, '');
      if (!lot || !result) return;
      const match = batches.find(entry => String(entry.lot).replace(/\s+/g, '') === lot);
      result.replaceChildren();
      if (match) {
        const line = document.createElement('span');
        line.textContent = text('found').replace('[lot]', match.lot).replace('[tested]', match.tested || '') + ' ';
        const link = document.createElement('a');
        link.href = match.url;
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = text('link');
        result.append(line, link);
      } else {
        result.textContent = text('missing').replace('[lot]', lot);
      }
    });
  }

  // Sticky add-to-cart bar once the main button scrolls away.
  const sticky = root.querySelector('[data-pdp-sticky]');
  const mainButton = root.querySelector('[data-pdp-main-add]');
  const finalBand = root.querySelector('[data-pdp-final]');
  if (sticky && mainButton && 'IntersectionObserver' in window) {
    let mainVisible = true;
    let pastMain = false;
    let finalVisible = false;
    const update = () => {
      const show = !mainVisible && pastMain && !finalVisible;
      sticky.hidden = !show;
      document.body.classList.toggle('pdp-sticky-on', show);
    };
    new IntersectionObserver(([entry]) => {
      mainVisible = entry.isIntersecting;
      pastMain = entry.boundingClientRect.top < 0;
      update();
    }).observe(mainButton);
    if (finalBand) new IntersectionObserver(([entry]) => { finalVisible = entry.isIntersecting; update(); }).observe(finalBand);
  }
})();
